import React, { useEffect, useState, Suspense, lazy } from 'react';
import { motion } from 'motion/react';
import { toast, Toaster } from 'sonner';
import { BadgeDollarSign, Gift, Trophy } from 'lucide-react';
import SplitText from './components/SplitText';
import ScrambledText from './components/ScrambledText';
import LoadingIndicator from './components/LoadingIndicator';
import TrackingPanel from './components/TrackingPanel';
import LeaderboardCard from './components/LeaderboardCard';
import WatchtimeLeaderboardCard from './components/WatchtimeLeaderboardCard';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './components/animate-ui/components/radix/dialog';
import { fetchShopItems, fetchRedemptions, fetchChatStats, createWebSocket, fetchLeaderboard, searchLeaderboard, fetchTrackingHistory, LeaderboardResponse } from './api';
import { ShopItem, Redemption } from './types';
import { useDebounce } from './hooks/use-debounce';
import { fetchUserProfilePic, getDefaultAvatarUrl } from './utils/profilePicCache';

const TRACKING_COOKIE_NAME = 'eazykee_tracking_username';

interface TrackingSummary {
  username: string;
  points: number | null;
}

function getCookie(name: string) {
  const value = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${name}=`))
    ?.split('=')
    .slice(1)
    .join('=') || '';

  try {
    return decodeURIComponent(value);
  } catch {
    return '';
  }
}

function formatPoints(value?: number | null) {
  return Number(value || 0).toLocaleString('en-US');
}

// Lazy load heavy components
const Plasma = lazy(() => import('./components/Plasma'));
const ShopGrid = lazy(() => import('./components/ShopGrid'));
const RedemptionFeed = lazy(() => import('./components/RedemptionFeed'));

function App() {
  const [items, setItems] = useState<ShopItem[]>([]);
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [highlightedItem, setHighlightedItem] = useState<string | undefined>(undefined);
  const [chatMessages, setChatMessages] = useState<number>(0);
  const [leaderboard, setLeaderboard] = useState<LeaderboardResponse>({ data: [], page: 1, pageSize: 50, totalEntries: 0, totalPages: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isLeaderboardOpen, setIsLeaderboardOpen] = useState(false);
  const [leaderboardProfilePics, setLeaderboardProfilePics] = useState<Record<string, string>>({});
  const [shouldAnimatePointLeaderboardList, setShouldAnimatePointLeaderboardList] = useState(false);
  const [isTrackingOpen, setIsTrackingOpen] = useState(false);
  const [trackingSummary, setTrackingSummary] = useState<TrackingSummary | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const isConnectedRef = React.useRef(false);
  const shouldAnimatePointLeaderboardListRef = React.useRef(false);
  const redemptionsSectionRef = React.useRef<HTMLDivElement | null>(null);
  const leaderboardSectionRef = React.useRef<HTMLDivElement | null>(null);
  const leaderboardRouteTimeoutRef = React.useRef<number | null>(null);
  const highlightTimeoutRef = React.useRef<number | null>(null);

  const normalizeItemKey = React.useCallback((value: string) => (
    String(value || '')
      .toLowerCase()
      .replace(/^\s*\([^)]*\)\s*/, '')
      .replace(/\s+/g, ' ')
      .trim()
  ), []);

  const normalizeUserName = React.useCallback((value: string) => (
    String(value || '').trim().toLowerCase()
  ), []);

  const handleLeaderboardOpenChange = React.useCallback((open: boolean) => {
    if (leaderboardRouteTimeoutRef.current !== null) {
      window.clearTimeout(leaderboardRouteTimeoutRef.current);
      leaderboardRouteTimeoutRef.current = null;
    }

    setIsLeaderboardOpen(open);
    if (open) {
      shouldAnimatePointLeaderboardListRef.current = true;
      setShouldAnimatePointLeaderboardList(true);
      window.history.pushState({}, '', '/leaderboard');
    } else {
      shouldAnimatePointLeaderboardListRef.current = false;
      setShouldAnimatePointLeaderboardList(false);
      leaderboardRouteTimeoutRef.current = window.setTimeout(() => {
        leaderboardRouteTimeoutRef.current = null;
        window.history.pushState({}, '', '/');
      }, 300);
      setSearchTerm('');
      setCurrentPage(1);
    }
  }, []);

  const handleTrackingUpdate = React.useCallback((summary: TrackingSummary) => {
    setTrackingSummary(summary);
  }, []);

  const scrollToSection = React.useCallback((target: React.RefObject<HTMLDivElement | null>) => {
    target.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, []);

  // Check URL on mount for /leaderboard route
  useEffect(() => {
    if (window.location.pathname === '/leaderboard') {
      shouldAnimatePointLeaderboardListRef.current = true;
      setShouldAnimatePointLeaderboardList(true);
      setIsLeaderboardOpen(true);
    }
  }, []);

  useEffect(() => {
    const savedUsername = getCookie(TRACKING_COOKIE_NAME);
    if (!savedUsername) return;

    let cancelled = false;

    async function loadTrackingSummary() {
      const data = await fetchTrackingHistory(savedUsername);
      if (!cancelled) {
        setTrackingSummary({
          username: data.username || savedUsername,
          points: data.latest?.points ?? null
        });
      }
    }

    loadTrackingSummary();
    const timer = window.setInterval(loadTrackingSummary, 60 * 1000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  // Initial Data Fetching - Optimized for faster load
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px), (pointer: coarse)');
    const update = () => setIsMobile(mq.matches);
    update();
    if (mq.addEventListener) {
      mq.addEventListener('change', update);
    } else {
      mq.addListener(update);
    }
    return () => {
      if (mq.removeEventListener) {
        mq.removeEventListener('change', update);
      } else {
        mq.removeListener(update);
      }
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        // Load shop items first (priority)
        const shopItems = await fetchShopItems();
        if (isMounted) {
          setItems(shopItems);
          setLoading(false);
        }

        // Load other data in background
        const [pastRedemptions, chatStats] = await Promise.all([
          fetchRedemptions(50),
          fetchChatStats()
        ]);

        if (isMounted) {
          setRedemptions(pastRedemptions);
          setChatMessages(chatStats);
          setError(false);
        }
      } catch (error) {
        console.error('Initialization failed:', error);
        if (isMounted) {
          setError(true);
          setLoading(false);
        }
      }
    };

    init();

    return () => {
      isMounted = false;
    };
  }, []);

  // Leaderboard fetching with search and pagination
  useEffect(() => {
    if (!isLeaderboardOpen) return; // Only fetch when leaderboard is open

    let cancelled = false;
    let animationTimer: number | null = null;

    const loadLeaderboard = async () => {
      setIsSearching(true);
      try {
        let data;
        if (debouncedSearchTerm.trim()) {
          data = await searchLeaderboard(debouncedSearchTerm, currentPage);
        } else {
          data = await fetchLeaderboard(currentPage);
        }
        if (cancelled) {
          return;
        }
        setLeaderboard(data);
        if (shouldAnimatePointLeaderboardListRef.current) {
          animationTimer = window.setTimeout(() => {
            shouldAnimatePointLeaderboardListRef.current = false;
            setShouldAnimatePointLeaderboardList(false);
          }, 420);
        }
      } catch (error) {
        console.error('Failed to load leaderboard:', error);
      } finally {
        if (!cancelled) {
          setIsSearching(false);
        }
      }
    };

    loadLeaderboard();

    return () => {
      cancelled = true;
      if (animationTimer !== null) {
        window.clearTimeout(animationTimer);
      }
    };
  }, [currentPage, debouncedSearchTerm, isLeaderboardOpen]);

  useEffect(() => {
    if (!isLeaderboardOpen || !leaderboard.data.length) return;

    let cancelled = false;

    async function fetchLeaderboardProfilePics() {
      const names = Array.from(new Set(leaderboard.data.map(entry => entry.name).filter(Boolean)));

      for (const name of names) {
        if (leaderboardProfilePics[name]) continue;

        try {
          const url = await fetchUserProfilePic(name);
          if (!cancelled) {
            setLeaderboardProfilePics(prev => ({ ...prev, [name]: url }));
          }
        } catch (error) {
          console.error(`Failed to fetch profile pic for ${name}:`, error);
        }
      }
    }

    fetchLeaderboardProfilePics();

    return () => {
      cancelled = true;
    };
  }, [isLeaderboardOpen, leaderboard.data, leaderboardProfilePics]);

  // WebSocket Connection
  useEffect(() => {
    const handleNewRedemption = async (redemption: Redemption) => {
      setRedemptions(prev => [redemption, ...prev].slice(0, 50));
      setItems(prev => prev.map(item => {
        const redeemedName = normalizeItemKey(redemption.item_name);
        const itemName = normalizeItemKey(item.name);
        const itemCode = normalizeItemKey(item.code);
        const matchesName = itemName === redeemedName || redeemedName.includes(itemName);
        const matchesCode = itemCode === redeemedName || redeemedName.includes(itemCode);

        if (!matchesName && !matchesCode) {
          return item;
        }

        return {
          ...item,
          redemptionCount: Number(item.redemptionCount || 0) + 1
        };
      }));

      // Show toast notification only if currently connected
      if (isConnectedRef.current) {
        toast(`${redemption.user_name} Most váltotta ki: ${redemption.item_name}`, {
          style: {
            background: 'white',
            color: 'black',
          },
        });
      }

      // Highlight the item
      setHighlightedItem(redemption.item_name);

      // Remove highlight after 5 seconds
      if (highlightTimeoutRef.current !== null) {
        window.clearTimeout(highlightTimeoutRef.current);
      }
      highlightTimeoutRef.current = window.setTimeout(() => {
        highlightTimeoutRef.current = null;
        setHighlightedItem(undefined);
      }, 5000);
    };

    const handleChatStats = (count: number) => {
      setChatMessages(count);
    };

    const ws = createWebSocket(
      handleNewRedemption,
      handleChatStats,
      () => {
        setConnected(true);
        isConnectedRef.current = true;
      },
      () => {
        setConnected(false);
        isConnectedRef.current = false;
      }
    );

    return () => {
      ws.close();
      if (highlightTimeoutRef.current !== null) {
        window.clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = null;
      }
    };
  }, [normalizeItemKey]);

  useEffect(() => () => {
    if (leaderboardRouteTimeoutRef.current !== null) {
      window.clearTimeout(leaderboardRouteTimeoutRef.current);
    }
  }, []);

  return (
    <div className="relative min-h-screen text-foreground overflow-hidden flex flex-col">
      {/* Toast Container */}
      <Toaster position="bottom-right" />

      {/* Background */}
      <div className="pointer-events-none fixed inset-0 z-0 bg-black [backface-visibility:hidden] [transform:translateZ(0)]">
        {isMobile ? (
          <div style={{ width: '100%', height: '100%', backgroundColor: '#0b0b0b' }} />
        ) : (
          <Suspense fallback={<div />}>
            <motion.div
              initial={false}
              animate={{ opacity: 1 }}
              transition={{ duration: 0 }}
              style={{ width: '100%', height: '100%' }}
            >
              <Plasma
                color="#ffffff"
                speed={0.5}
                direction="forward"
                scale={1}
                opacity={0.25}
                mouseInteractive={false}
              />
            </motion.div>
          </Suspense>
        )}
      </div>

      {/* Main Content */}
      <div className="relative z-10 container mx-auto flex flex-grow flex-col px-3 pt-5 sm:px-6 sm:pt-8 md:px-4">

        {/* Header with buttons */}
        <div className="absolute left-3 top-3 z-20 md:left-8 md:top-8">
          <button
            type="button"
            onClick={() => window.open('https://kicklet.app/user/eazykeee/shop', '_blank')}
            className="rounded-full bg-white px-4 py-1.5 text-sm font-semibold text-black transition hover:bg-white/90 active:scale-[0.98] md:px-8 md:py-2 md:text-base"
          >
            Shop
          </button>
        </div>

        <div className="absolute right-3 top-3 z-20 flex max-w-[64vw] items-center justify-end gap-2 md:right-8 md:top-8 md:max-w-none">
          {trackingSummary?.username && (
            <div className="hidden max-w-[36vw] items-center gap-2 truncate rounded-full border border-white/10 bg-black/55 px-3 py-2 text-xs font-semibold text-white/85 backdrop-blur-md md:flex md:max-w-none md:px-4 md:text-sm">
              <span className="truncate tabular-nums">{formatPoints(trackingSummary.points)} pont</span>
            </div>
          )}
          <button
            type="button"
            onClick={() => setIsTrackingOpen(true)}
            className="rounded-full bg-white px-4 py-1.5 text-sm font-semibold text-black transition hover:bg-white/90 active:scale-[0.98] md:px-8 md:py-2 md:text-base"
          >
            Tracking
          </button>
        </div>

        <div className="absolute left-1/2 top-[3.8rem] z-20 flex w-[calc(100%-1.5rem)] -translate-x-1/2 items-center justify-center gap-2 md:hidden">
          <button
            type="button"
            onClick={() => scrollToSection(leaderboardSectionRef)}
            className="flex h-9 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-full border border-white/10 bg-white px-3 text-xs font-semibold text-black shadow-[0_8px_24px_rgba(0,0,0,0.28)] transition hover:bg-white/90 active:scale-[0.98]"
          >
            <Trophy size={14} />
            <span>Leaderboard</span>
          </button>
          <button
            type="button"
            onClick={() => scrollToSection(redemptionsSectionRef)}
            className="flex h-9 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-full border border-white/10 bg-white px-3 text-xs font-semibold text-black shadow-[0_8px_24px_rgba(0,0,0,0.28)] transition hover:bg-white/90 active:scale-[0.98]"
          >
            <Gift size={14} />
            <span>Legutóbbi</span>
          </button>
        </div>

        <Dialog open={isTrackingOpen} onOpenChange={setIsTrackingOpen}>
          <DialogContent
            className="max-h-[86dvh] max-w-[calc(100%-0.75rem)] overflow-y-auto rounded-2xl border border-white/10 bg-black/95 p-3 md:max-h-[90dvh] md:max-w-2xl md:p-6"
          >
            <DialogHeader className="mb-3">
              <DialogTitle>Pont Tracking</DialogTitle>
              <DialogDescription className="sr-only">
                Tracking megkezdése után a rendszer elkezdi nyomon követni a pontszámodat.
              </DialogDescription>
            </DialogHeader>
            <TrackingPanel onTrackingUpdate={handleTrackingUpdate} />
          </DialogContent>
        </Dialog>

        <Dialog open={isLeaderboardOpen} onOpenChange={handleLeaderboardOpenChange}>
          <DialogContent className="flex h-[86dvh] max-w-[calc(100%-0.75rem)] flex-col gap-3 rounded-2xl border border-white/10 bg-black/95 p-3 md:h-[80vh] md:max-w-2xl md:gap-4 md:p-6" style={{ WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' }}>
            <DialogHeader>
              <DialogTitle>Point Leaderboard</DialogTitle>
            </DialogHeader>

              {/* Search Input */}
              <div className="relative">
                <input
                  type="text"
                  aria-label="Leaderboard keresés"
                  placeholder="Keresés név alapján..."
                  value={searchTerm}
                  onChange={(e) => {
                    shouldAnimatePointLeaderboardListRef.current = false;
                    setShouldAnimatePointLeaderboardList(false);
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-white transition placeholder:text-white/40 focus:border-white/30 focus:outline-none"
                />
                {isSearching && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <LoadingIndicator compact />
                  </div>
                )}
              </div>

              {leaderboard.topSpender && (
                <div className="flex items-center justify-between gap-3 rounded-2xl border border-emerald-300/20 bg-emerald-300/[0.08] px-4 py-2 text-sm text-emerald-100">
                  <div className="flex min-w-0 items-center gap-2">
                    <BadgeDollarSign size={17} className="shrink-0 text-emerald-200" />
                    <span className="truncate font-semibold">Top spender: {leaderboard.topSpender.user_name}</span>
                  </div>
                  <span className="shrink-0 font-bold tabular-nums">{leaderboard.topSpender.count}x</span>
                </div>
              )}

              {/* Leaderboard List */}
              <div className="flex-1 overflow-y-auto pr-2">
                  <motion.div
                    initial={shouldAnimatePointLeaderboardList ? { opacity: 0, filter: 'blur(10px)', clipPath: 'inset(0 0 14% 0)' } : false}
                    animate={{
                      opacity: isSearching ? 0.72 : 1,
                      filter: 'blur(0px)',
                      clipPath: 'inset(0 0 0% 0)'
                    }}
                    transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                    className="space-y-2"
                    style={{
                      WebkitFontSmoothing: 'subpixel-antialiased',
                      MozOsxFontSmoothing: 'auto',
                      transform: 'translateZ(0)',
                      backfaceVisibility: 'hidden',
                      perspective: 1000
                    }}
                  >
                    {leaderboard.data.length > 0 ? (
                      leaderboard.data.map((entry, index) => {
                        const displayRank = entry.rank || ((currentPage - 1) * leaderboard.pageSize + index + 1);
                        const isTopSpender = leaderboard.topSpender?.normalized === normalizeUserName(entry.name);
                        return (
                          <motion.div
                            key={`${entry.name}-${entry.rank || displayRank}`}
                            initial={shouldAnimatePointLeaderboardList ? { opacity: 0, x: -10, scale: 0.985 } : false}
                            animate={{ opacity: 1, x: 0, scale: 1 }}
                            transition={{
                              duration: 0.28,
                              delay: shouldAnimatePointLeaderboardList ? Math.min(index, 12) * 0.025 : 0,
                              ease: [0.22, 1, 0.36, 1]
                            }}
                            className={`flex items-center justify-between gap-2.5 rounded-[3rem] bg-white/5 px-2.5 py-2 backdrop-blur-sm transition-colors duration-200 hover:bg-white/10 md:gap-3 md:px-3 ${index === 0 ? 'ring-1 ring-white/20' : ''}`}
                            style={{
                              boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.1), 0 4px 6px rgba(0, 0, 0, 0.1)'
                            }}
                          >
                            <div className="flex min-w-0 items-center gap-2.5 md:gap-3">
                              <div className="relative h-9 w-9 shrink-0 overflow-hidden rounded-full bg-white/10 ring-1 ring-white/10 md:h-10 md:w-10">
                                <img
                                  src={leaderboardProfilePics[entry.name] || getDefaultAvatarUrl(entry.name)}
                                  alt={entry.name}
                                  loading="lazy"
                                  decoding="async"
                                  className="h-full w-full object-cover"
                                  onError={(event) => {
                                    event.currentTarget.src = getDefaultAvatarUrl(entry.name);
                                  }}
                                />
                              </div>
                              <div className="min-w-0">
                                <div className="flex min-w-0 items-center gap-1.5">
                                  <span className="truncate font-medium text-white/90">{entry.name}</span>
                                  {isTopSpender && (
                                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-300/20 bg-emerald-300/[0.1] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-100">
                                      <BadgeDollarSign size={11} />
                                      Spender
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-white/50">#{displayRank}</div>
                              </div>
                            </div>
                            <span className="shrink-0 text-xs font-semibold tabular-nums text-white/80 md:text-sm">
                              {entry.points.toLocaleString()}
                            </span>
                          </motion.div>
                        );
                      })
                    ) : (
                      <p className="text-white/60 text-center py-8">
                        {searchTerm ? 'Nincs találat' : 'Nincs leaderboard adat'}
                      </p>
                    )}
                  </motion.div>
              </div>

              {/* Pagination */}
              {leaderboard.totalPages > 1 && (
                <div className="flex items-center justify-between pt-2 border-t border-white/10">
                  <button
                    type="button"
                    onClick={() => {
                      shouldAnimatePointLeaderboardListRef.current = false;
                      setShouldAnimatePointLeaderboardList(false);
                      setCurrentPage(prev => Math.max(1, prev - 1));
                    }}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-white/5 rounded-lg hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition"
                  >
                    ← Előző
                  </button>

                  <div className="flex items-center gap-2">
                    {/* First page */}
                    {currentPage > 2 && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            shouldAnimatePointLeaderboardListRef.current = false;
                            setShouldAnimatePointLeaderboardList(false);
                            setCurrentPage(1);
                          }}
                          className="px-3 py-1 bg-white/5 rounded-lg hover:bg-white/10 transition"
                        >
                          1
                        </button>
                        {currentPage > 3 && <span className="text-white/40">...</span>}
                      </>
                    )}

                    {/* Previous page */}
                    {currentPage > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          shouldAnimatePointLeaderboardListRef.current = false;
                          setShouldAnimatePointLeaderboardList(false);
                          setCurrentPage(currentPage - 1);
                        }}
                        className="px-3 py-1 bg-white/5 rounded-lg hover:bg-white/10 transition"
                      >
                        {currentPage - 1}
                      </button>
                    )}

                    {/* Current page */}
                    <button type="button" className="px-3 py-1 bg-white/20 rounded-lg font-semibold">
                      {currentPage}
                    </button>

                    {/* Next page */}
                    {currentPage < leaderboard.totalPages && (
                      <button
                        type="button"
                        onClick={() => {
                          shouldAnimatePointLeaderboardListRef.current = false;
                          setShouldAnimatePointLeaderboardList(false);
                          setCurrentPage(currentPage + 1);
                        }}
                        className="px-3 py-1 bg-white/5 rounded-lg hover:bg-white/10 transition"
                      >
                        {currentPage + 1}
                      </button>
                    )}

                    {/* Last page */}
                    {currentPage < leaderboard.totalPages - 1 && (
                      <>
                        {currentPage < leaderboard.totalPages - 2 && <span className="text-white/40">...</span>}
                        <button
                          type="button"
                          onClick={() => {
                            shouldAnimatePointLeaderboardListRef.current = false;
                            setShouldAnimatePointLeaderboardList(false);
                            setCurrentPage(leaderboard.totalPages);
                          }}
                          className="px-3 py-1 bg-white/5 rounded-lg hover:bg-white/10 transition"
                        >
                          {leaderboard.totalPages}
                        </button>
                      </>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      shouldAnimatePointLeaderboardListRef.current = false;
                      setShouldAnimatePointLeaderboardList(false);
                      setCurrentPage(prev => Math.min(leaderboard.totalPages, prev + 1));
                    }}
                    disabled={currentPage === leaderboard.totalPages}
                    className="px-4 py-2 bg-white/5 rounded-lg hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition"
                  >
                    Következő →
                  </button>
                </div>
              )}
          </DialogContent>
        </Dialog>

        {/* Header */}
        <header className="mb-6 pt-28 text-center md:mb-12 md:pt-0">
          <SplitText
            text="Eazykee Store Monitor"
            duration={0.8}
            className="mb-2 text-[2rem] font-bold leading-none tracking-tighter md:mb-4 md:text-6xl"
          />
          <p className="px-2 text-sm leading-relaxed text-white/60 md:text-xl">
            Eazykee store kiváltások tracker
          </p>
        </header>

        <div className="mx-auto grid w-full max-w-[2000px] grid-cols-1 gap-5 md:gap-8 xl:grid-cols-10">

          {/* Left Column: Shop Grid (Takes up 7/10 space on large screens) */}
          <div className="xl:col-span-7">
            {!loading && items.length > 0 && (
              <div className="mb-3 md:mb-6">
                <h2 className="text-xl md:text-2xl font-semibold">itemek</h2>
              </div>
            )}
            {error ? (
              <div className="flex flex-col items-center justify-center py-16 space-y-4">
                <ScrambledText className="text-2xl md:text-4xl font-bold text-red-400">
                  Adatok betöltése sikertelen
                </ScrambledText>
                <ScrambledText className="text-lg md:text-2xl text-white/60">
                  Szerverhez nem sikerült kapcsolódni
                </ScrambledText>
              </div>
            ) : (
              <Suspense fallback={<LoadingIndicator label="Store betöltése" />}>
                <ShopGrid
                  items={items}
                  loading={loading}
                  highlightedItem={highlightedItem}
                />
              </Suspense>
            )}
          </div>

          {/* Right Column: Live Feed (Takes up 3/10 space on large screens) */}
          <div className="xl:col-span-3">
            {/* Spacer to align with items header */}
            {!loading && items.length > 0 && (
              <div className="mb-4 md:mb-6 h-7 md:h-8" />
            )}
            <div className="space-y-5 xl:sticky xl:top-8 xl:space-y-6">
              <div ref={redemptionsSectionRef} className="scroll-mt-24 md:scroll-mt-8">
                <Suspense fallback={<LoadingIndicator label="Kapcsolódás a backendhez" />}>
                  <RedemptionFeed
                    redemptions={redemptions}
                    connected={connected}
                    chatMessages={chatMessages}
                  />
                </Suspense>
              </div>
              <div ref={leaderboardSectionRef} className="scroll-mt-24 md:scroll-mt-8">
                <LeaderboardCard onOpen={() => handleLeaderboardOpenChange(true)} />
              </div>
              <WatchtimeLeaderboardCard />
            </div>
          </div>

        </div>

        {/* Footer */}
        <footer className="mt-auto pt-6 pb-2 text-center">
          <p className="text-xs text-white/30">ai generated site</p>
        </footer>
      </div>
    </div>
  );
}

export default App;
